import React, { useState, useEffect, useRef, useMemo } from 'react'
import { bool, number, oneOfType, shape, string } from 'prop-types'
import { trackEvent } from '@fs/zion-analytics'
import { css } from '@emotion/core'
import TileText from './TileText'
import AncestorDetails from './TileContent/AncestorDetails/AncestorDetails'

export default function Tile({ tileData, color, autoload, setOpenTile, overlay, openTile }) {
  const { person } = tileData
  const tileRef = useRef()
  const [transitioned, setTransitioned] = useState(false)

  const [openAncestorDetails, setOpenAncestorDetails] = useState(false)
  const { isOpen, transitioning } = overlay
  const params = useMemo(() => new URLSearchParams(window.location.search), [])

  useEffect(() => {
    if (!isOpen && transitioning && params?.get('experience')) {
      params.delete('experience')
      window.history.replaceState({}, '', decodeURIComponent(`${window.location.pathname}?${params}`))
    }
  }, [isOpen, params, transitioning])

  const masonryItemCss = css`
    cursor: pointer;
    display: inline-block;
    background-color: ${color};
    height: 100%;
    border-radius: 8px;
    border: none;
    &:hover {
      box-shadow:
        0px 1px 20px 0px rgba(0, 0, 0, 0.2),
        0px 2px 2px 0px rgba(0, 0, 0, 0.14),
        0px 3px 1px -2px rgba(0, 0, 0, 0.12);
    }
  `
  useEffect(() => {
    if (isOpen && !transitioning) {
      setTransitioned(true)
    }
    if (!isOpen && !transitioning && transitioned) {
      trackEvent({
        action: 'click_action',
        link_name: `DS_ALL_ABOUT_ME: DS_AAM_CLOSE_${tileData?.supportedTileId?.toUpperCase()}`,
      })
      setTransitioned(false)
    }
  }, [isOpen, transitioning, setTransitioned, transitioned, tileData.supportedTileId])

  const handleClick = (tile) => {
    if (tile?.type === 'ancestorDetails') {
      setOpenAncestorDetails(true)
    } else {
      window.history.replaceState({}, '', decodeURIComponent(`${window.location.pathname}?${params}`))
      setOpenTile({ ...tile, highlightColor: color })
      overlay.open()
    }
    params.set('experience', `${tile?.title}+${tile?.id}`)
  }
  const autoloader = () => {
    if (autoload && params?.get('experience') && !isOpen && openTile?.id !== tileData?.id) {
      setOpenTile({ ...tileData, highlightColor: color })
      overlay.open()
    }
  }
  // Only depend on primitive values to avoid unnecessary re-runs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(autoloader, [autoload, color, isOpen, params, openTile?.id, tileData?.id])

  return (
    <>
      <button
        type="button"
        css={masonryItemCss}
        key={tileData?.id}
        onClick={() => {
          trackEvent({
            action: 'click_action',
            link_name: `DS_ALL_ABOUT_ME: DS_AAM_SHOW_${tileData?.supportedTileId?.toUpperCase()}`,
          })
          handleClick(tileData)
        }}
        ref={tileRef}
        data-testid={tileData?.id}
      >
        <TileText
          icon={tileData?.icon}
          color={color}
          title={tileData?.title}
          header={tileData?.header}
          subHeading={tileData?.additionalText}
        />
      </button>
      {openAncestorDetails && <AncestorDetails person={person} setOpenAncestorDetails={setOpenAncestorDetails} />}
    </>
  )
}

Tile.propTypes = {
  tileData: shape({
    header: string,
    icon: string,
    id: number,
    imageUrl: string,
    language: string,
    parsedNames: shape({}),
    region: string,
    title: string,
    type: string,
    year: oneOfType([string, number]),
  }),
  color: string,
  autoload: bool,
  openTile: shape({}),
}
